/**
 * Question Keywords and AI Overview Exposure in B2B 2026 — /data/question-keywords-ai-overview-rate
 * 80 B2B question-format keywords analyzed for AI Overview exposure and CTR impact
 */
import React, { useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { datasetJsonLd } from "@/lib/datasetSchema";
import { useIsMobile } from "@/hooks/useIsMobile";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";

type QueryType = "What is" | "How to" | "Best" | "Compare";

interface QuestionRow {
  keyword: string;
  queryType: QueryType;
  industry: string;
  aiOverview: boolean;
  organicCTR: number;
  kd: number;
}

const DATA: QuestionRow[] = [
  // "What is" — 20 keywords, ~18 with AI Overview, CTR 24-35% with AIO, 42-58% without
  { keyword: "what is saas", queryType: "What is", industry: "SaaS", aiOverview: true, organicCTR: 28, kd: 52 },
  { keyword: "what is a crm", queryType: "What is", industry: "SaaS", aiOverview: true, organicCTR: 26, kd: 54 },
  { keyword: "what is marketing automation", queryType: "What is", industry: "SaaS", aiOverview: true, organicCTR: 27, kd: 50 },
  { keyword: "what is revenue operations", queryType: "What is", industry: "SaaS", aiOverview: true, organicCTR: 29, kd: 46 },
  { keyword: "what is open banking", queryType: "What is", industry: "Fintech", aiOverview: true, organicCTR: 25, kd: 51 },
  { keyword: "what is embedded finance", queryType: "What is", industry: "Fintech", aiOverview: true, organicCTR: 30, kd: 44 },
  { keyword: "what is accounts payable", queryType: "What is", industry: "Fintech", aiOverview: true, organicCTR: 31, kd: 40 },
  { keyword: "what is an ats", queryType: "What is", industry: "HR Tech", aiOverview: true, organicCTR: 27, kd: 47 },
  { keyword: "what is workforce management", queryType: "What is", industry: "HR Tech", aiOverview: true, organicCTR: 28, kd: 38 },
  { keyword: "what is employee engagement", queryType: "What is", industry: "HR Tech", aiOverview: true, organicCTR: 26, kd: 43 },
  { keyword: "what is zero trust security", queryType: "What is", industry: "Cybersecurity", aiOverview: true, organicCTR: 25, kd: 53 },
  { keyword: "what is siem", queryType: "What is", industry: "Cybersecurity", aiOverview: true, organicCTR: 28, kd: 49 },
  { keyword: "what is endpoint detection", queryType: "What is", industry: "Cybersecurity", aiOverview: true, organicCTR: 29, kd: 45 },
  { keyword: "what is a learning management system", queryType: "What is", industry: "EdTech", aiOverview: true, organicCTR: 27, kd: 50 },
  { keyword: "what is microlearning", queryType: "What is", industry: "EdTech", aiOverview: true, organicCTR: 32, kd: 36 },
  { keyword: "what is business intelligence", queryType: "What is", industry: "Data & Analytics", aiOverview: true, organicCTR: 26, kd: 57 },
  { keyword: "what is a data warehouse", queryType: "What is", industry: "Data & Analytics", aiOverview: true, organicCTR: 25, kd: 55 },
  { keyword: "what is product-led growth", queryType: "What is", industry: "SaaS", aiOverview: true, organicCTR: 30, kd: 42 },
  { keyword: "what is proptech", queryType: "What is", industry: "PropTech", aiOverview: false, organicCTR: 48, kd: 38 },
  { keyword: "what is contract lifecycle management", queryType: "What is", industry: "Legal Tech", aiOverview: false, organicCTR: 52, kd: 42 },

  // "How to" — 20 keywords, ~17 with AI Overview
  { keyword: "how to implement zero trust", queryType: "How to", industry: "Cybersecurity", aiOverview: true, organicCTR: 31, kd: 56 },
  { keyword: "how to choose a crm", queryType: "How to", industry: "SaaS", aiOverview: true, organicCTR: 29, kd: 52 },
  { keyword: "how to reduce payroll errors", queryType: "How to", industry: "HR Tech", aiOverview: true, organicCTR: 33, kd: 44 },
  { keyword: "how to build a sales funnel", queryType: "How to", industry: "SaaS", aiOverview: true, organicCTR: 28, kd: 58 },
  { keyword: "how to improve employee retention", queryType: "How to", industry: "HR Tech", aiOverview: true, organicCTR: 30, kd: 50 },
  { keyword: "how to prevent ransomware attacks", queryType: "How to", industry: "Cybersecurity", aiOverview: true, organicCTR: 34, kd: 48 },
  { keyword: "how to set up elearning", queryType: "How to", industry: "EdTech", aiOverview: true, organicCTR: 32, kd: 42 },
  { keyword: "how to create a data strategy", queryType: "How to", industry: "Data & Analytics", aiOverview: true, organicCTR: 30, kd: 55 },
  { keyword: "how to automate accounts payable", queryType: "How to", industry: "Fintech", aiOverview: true, organicCTR: 28, kd: 46 },
  { keyword: "how to measure content roi", queryType: "How to", industry: "SaaS", aiOverview: true, organicCTR: 35, kd: 40 },
  { keyword: "how to scale a saas business", queryType: "How to", industry: "SaaS", aiOverview: true, organicCTR: 29, kd: 54 },
  { keyword: "how to write a sales playbook", queryType: "How to", industry: "SaaS", aiOverview: true, organicCTR: 33, kd: 38 },
  { keyword: "how to run a security audit", queryType: "How to", industry: "Cybersecurity", aiOverview: true, organicCTR: 31, kd: 52 },
  { keyword: "how to implement an lms", queryType: "How to", industry: "EdTech", aiOverview: true, organicCTR: 30, kd: 44 },
  { keyword: "how to calculate churn rate", queryType: "How to", industry: "SaaS", aiOverview: true, organicCTR: 28, kd: 42 },
  { keyword: "how to build a data pipeline", queryType: "How to", industry: "Data & Analytics", aiOverview: true, organicCTR: 32, kd: 58 },
  { keyword: "how to reduce invoice processing time", queryType: "How to", industry: "Fintech", aiOverview: true, organicCTR: 34, kd: 38 },
  { keyword: "how to manage remote teams", queryType: "How to", industry: "HR Tech", aiOverview: false, organicCTR: 49, kd: 46 },
  { keyword: "how to migrate to the cloud", queryType: "How to", industry: "SaaS", aiOverview: false, organicCTR: 53, kd: 50 },
  { keyword: "how to choose property management software", queryType: "How to", industry: "PropTech", aiOverview: false, organicCTR: 46, kd: 40 },

  // "Best" — 20 keywords, ~13 with AI Overview
  { keyword: "best crm software for b2b", queryType: "Best", industry: "SaaS", aiOverview: true, organicCTR: 37, kd: 66 },
  { keyword: "best hr software for small business", queryType: "Best", industry: "HR Tech", aiOverview: true, organicCTR: 38, kd: 60 },
  { keyword: "best siem tools for enterprise", queryType: "Best", industry: "Cybersecurity", aiOverview: true, organicCTR: 36, kd: 70 },
  { keyword: "best lms for corporate training", queryType: "Best", industry: "EdTech", aiOverview: true, organicCTR: 39, kd: 63 },
  { keyword: "best b2b payment software", queryType: "Best", industry: "Fintech", aiOverview: true, organicCTR: 37, kd: 64 },
  { keyword: "best accounts payable software", queryType: "Best", industry: "Fintech", aiOverview: true, organicCTR: 38, kd: 62 },
  { keyword: "best data visualization tools", queryType: "Best", industry: "Data & Analytics", aiOverview: true, organicCTR: 36, kd: 65 },
  { keyword: "best ats for startups", queryType: "Best", industry: "HR Tech", aiOverview: true, organicCTR: 40, kd: 58 },
  { keyword: "best warehouse management software", queryType: "Best", industry: "Logistics", aiOverview: true, organicCTR: 37, kd: 56 },
  { keyword: "best property management software", queryType: "Best", industry: "PropTech", aiOverview: true, organicCTR: 39, kd: 62 },
  { keyword: "best contract management software", queryType: "Best", industry: "Legal Tech", aiOverview: true, organicCTR: 38, kd: 60 },
  { keyword: "best endpoint security software", queryType: "Best", industry: "Cybersecurity", aiOverview: true, organicCTR: 36, kd: 68 },
  { keyword: "best elearning authoring tools", queryType: "Best", industry: "EdTech", aiOverview: true, organicCTR: 41, kd: 55 },
  { keyword: "best saas tools for remote teams", queryType: "Best", industry: "SaaS", aiOverview: false, organicCTR: 52, kd: 52 },
  { keyword: "best expense tracking software", queryType: "Best", industry: "Fintech", aiOverview: false, organicCTR: 49, kd: 58 },
  { keyword: "best project management tool", queryType: "Best", industry: "SaaS", aiOverview: false, organicCTR: 46, kd: 72 },
  { keyword: "best fleet management software", queryType: "Best", industry: "Logistics", aiOverview: false, organicCTR: 54, kd: 54 },
  { keyword: "best payroll software 2026", queryType: "Best", industry: "HR Tech", aiOverview: false, organicCTR: 48, kd: 62 },
  { keyword: "best vulnerability scanner tools", queryType: "Best", industry: "Cybersecurity", aiOverview: false, organicCTR: 51, kd: 60 },
  { keyword: "best healthcare practice management", queryType: "Best", industry: "Health Tech", aiOverview: false, organicCTR: 53, kd: 58 },

  // "Compare" — 20 keywords, ~8 with AI Overview
  { keyword: "salesforce vs hubspot comparison", queryType: "Compare", industry: "SaaS", aiOverview: true, organicCTR: 42, kd: 65 },
  { keyword: "quickbooks vs xero vs wave", queryType: "Compare", industry: "Fintech", aiOverview: true, organicCTR: 40, kd: 67 },
  { keyword: "tableau vs power bi vs looker", queryType: "Compare", industry: "Data & Analytics", aiOverview: true, organicCTR: 38, kd: 72 },
  { keyword: "workday vs bamboohr comparison", queryType: "Compare", industry: "HR Tech", aiOverview: true, organicCTR: 43, kd: 60 },
  { keyword: "crowdstrike vs sentinelone", queryType: "Compare", industry: "Cybersecurity", aiOverview: true, organicCTR: 41, kd: 62 },
  { keyword: "docebo vs cornerstone comparison", queryType: "Compare", industry: "EdTech", aiOverview: true, organicCTR: 44, kd: 58 },
  { keyword: "stripe vs braintree comparison", queryType: "Compare", industry: "Fintech", aiOverview: true, organicCTR: 39, kd: 60 },
  { keyword: "asana vs monday vs clickup", queryType: "Compare", industry: "SaaS", aiOverview: true, organicCTR: 37, kd: 70 },
  { keyword: "sap vs oracle erp comparison", queryType: "Compare", industry: "SaaS", aiOverview: false, organicCTR: 55, kd: 68 },
  { keyword: "greenhouse vs lever comparison", queryType: "Compare", industry: "HR Tech", aiOverview: false, organicCTR: 58, kd: 56 },
  { keyword: "palo alto vs fortinet comparison", queryType: "Compare", industry: "Cybersecurity", aiOverview: false, organicCTR: 53, kd: 64 },
  { keyword: "aws vs azure vs gcp comparison", queryType: "Compare", industry: "SaaS", aiOverview: false, organicCTR: 50, kd: 72 },
  { keyword: "snowflake vs databricks", queryType: "Compare", industry: "Data & Analytics", aiOverview: false, organicCTR: 56, kd: 66 },
  { keyword: "zendesk vs freshdesk", queryType: "Compare", industry: "SaaS", aiOverview: false, organicCTR: 52, kd: 62 },
  { keyword: "procore vs autodesk construction", queryType: "Compare", industry: "PropTech", aiOverview: false, organicCTR: 57, kd: 58 },
  { keyword: "clm platform comparison", queryType: "Compare", industry: "Legal Tech", aiOverview: false, organicCTR: 54, kd: 60 },
  { keyword: "rippling vs gusto comparison", queryType: "Compare", industry: "HR Tech", aiOverview: false, organicCTR: 51, kd: 58 },
  { keyword: "netsuite vs sage intacct", queryType: "Compare", industry: "Fintech", aiOverview: false, organicCTR: 55, kd: 62 },
  { keyword: "coupa vs ariba procurement", queryType: "Compare", industry: "Fintech", aiOverview: false, organicCTR: 57, kd: 60 },
  { keyword: "loadsmart vs echo global logistics", queryType: "Compare", industry: "Logistics", aiOverview: false, organicCTR: 53, kd: 55 },
];

const queryTypeColors: Record<QueryType, string> = {
  "What is": "#ef4444",
  "How to": "#f59e0b",
  "Best": "#f97316",
  "Compare": "#22c55e",
};

const selectStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "8px", color: "white", fontFamily: ff, fontSize: "14px",
  padding: "10px 32px 10px 14px", outline: "none", cursor: "pointer",
  appearance: "none", WebkitAppearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2 4l4 4 4-4' stroke='rgba(255,255,255,0.4)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
};
const thStyle: React.CSSProperties = {
  padding: "13px 16px", textAlign: "left" as const, fontSize: "11px", fontWeight: 700,
  fontFamily: ff, letterSpacing: "0.07em", textTransform: "uppercase" as const,
  color: "rgba(255,255,255,0.4)", borderBottom: "1px solid rgba(255,255,255,0.07)",
  whiteSpace: "nowrap" as const, cursor: "pointer", userSelect: "none" as const,
};
const tdStyle: React.CSSProperties = { padding: "14px 16px", verticalAlign: "middle", borderBottom: "1px solid rgba(255,255,255,0.04)" };

function SortArrow({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active) return <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "10px", marginLeft: "4px" }}>⇅</span>;
  return <span style={{ color: cyan, fontSize: "10px", marginLeft: "4px" }}>{dir === "asc" ? "↑" : "↓"}</span>;
}

type SortKey = "keyword" | "queryType" | "industry" | "organicCTR" | "kd";

export default function QuestionKeywordsAiOverview() {
  useSeoMeta({
    title: "Question Keywords and AI Overview Exposure in B2B 2026",
    description: "AI Overview trigger rates for 80 B2B question-format keywords — 'What is', 'How to', 'Best', and 'Compare' queries, with organic CTR impact data.",
    canonical: "/data/question-keywords-ai-overview-rate",
    jsonLd: datasetJsonLd({ title: "Question Keywords and AI Overview Exposure in B2B 2026", description: "AI Overview trigger rates for 80 B2B question-format keywords — 'What is', 'How to', 'Best', and 'Compare' queries, with organic CTR impact data.", path: "/data/question-keywords-ai-overview-rate" }),
  });
  const isMobile = useIsMobile();

  const [queryTypeFilter, setQueryTypeFilter] = useState("All");
  const [industryFilter, setIndustryFilter] = useState("All");
  const [aiFilter, setAiFilter] = useState("All");
  const [sortKey, setSortKey] = useState<SortKey>("keyword");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const industries = useMemo(() => ["All", ...Array.from(new Set(DATA.map(r => r.industry)))], []);
  const queryTypes: ("All" | QueryType)[] = ["All", "What is", "How to", "Best", "Compare"];

  const chartData = useMemo(() => {
    const types: QueryType[] = ["What is", "How to", "Best", "Compare"];
    return types.map(qt => {
      const rows = DATA.filter(r => r.queryType === qt);
      const aiCount = rows.filter(r => r.aiOverview).length;
      const rate = Math.round((aiCount / rows.length) * 100);
      return { queryType: qt, aiRate: rate };
    });
  }, []);

  const filtered = useMemo(() => {
    let rows = [...DATA];
    if (queryTypeFilter !== "All") rows = rows.filter(r => r.queryType === queryTypeFilter);
    if (industryFilter !== "All") rows = rows.filter(r => r.industry === industryFilter);
    if (aiFilter === "Yes") rows = rows.filter(r => r.aiOverview);
    if (aiFilter === "No") rows = rows.filter(r => !r.aiOverview);
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
      return sortDir === "asc" ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return rows;
  }, [queryTypeFilter, industryFilter, aiFilter, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  const barColors: Record<string, string> = {
    "What is": "#ef4444",
    "How to": "#f59e0b",
    "Best": "#f97316",
    "Compare": "#22c55e",
  };

  return (
    <SiteLayout>
      {/* Hero */}
      <section style={{ background: "#0a0a0a", padding: isMobile ? "140px 20px 56px" : "180px 72px 72px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
            <a href="/data" style={{ color: "rgba(255,255,255,0.4)", fontFamily: ff, fontSize: "13px", textDecoration: "none" }}>Data</a>
            <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "13px" }}>/</span>
            <span style={{ color: "rgba(255,255,255,0.6)", fontFamily: ff, fontSize: "13px" }}>Question Keywords & AI Overview</span>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "rgba(87,215,226,0.08)", border: "1px solid rgba(87,215,226,0.2)", borderRadius: "20px", padding: "5px 14px", marginBottom: "24px" }}>
            <span style={{ color: cyan, fontSize: "12px", fontFamily: ff, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Original Data</span>
          </div>
          <h1 style={{ color: "white", fontSize: isMobile ? "32px" : "52px", fontWeight: 800, fontFamily: ff, lineHeight: 1.1, marginBottom: "20px", letterSpacing: "-0.02em" }}>
            Question Keywords and AI Overview<br />Exposure in B2B 2026
          </h1>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: isMobile ? "16px" : "19px", fontFamily: ff, lineHeight: 1.65, maxWidth: "680px", marginBottom: "48px" }}>
            Not all question formats are equally affected. "What is" queries face near-total AI Overview capture. "Compare" queries largely don't. 80 B2B keywords examined, March 2026.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "24px" }}>
            {[
              { val: "90%", label: "of 'What is' B2B queries trigger an AI Overview" },
              { val: "40%", label: "of 'Compare' queries trigger an AI Overview" },
              { val: "80", label: "question-format keywords analyzed" },
            ].map(s => (
              <div key={s.label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px", padding: "24px" }}>
                <div style={{ color: cyan, fontSize: "36px", fontWeight: 800, fontFamily: ff, lineHeight: 1 }}>{s.val}</div>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "14px", fontFamily: ff, marginTop: "8px", lineHeight: 1.4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chart */}
      <section style={{ background: "#0a0a0a", padding: isMobile ? "40px 20px" : "56px 72px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <h2 style={{ color: "white", fontSize: "20px", fontWeight: 700, fontFamily: ff, marginBottom: "32px" }}>AI Overview Rate (%) by Query Type</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="queryType" tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 13, fontFamily: ff }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11, fontFamily: ff }} axisLine={false} tickLine={false} unit="%" domain={[0, 100]} />
              <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", fontFamily: ff, fontSize: "13px" }} labelStyle={{ color: "white" }} itemStyle={{ color: "rgba(255,255,255,0.7)" }} formatter={(value: number) => [`${value}%`, "AI Overview Rate"]} />
              <Bar dataKey="aiRate" radius={[3, 3, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={barColors[entry.queryType] ?? cyan} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Table */}
      <section style={{ background: "#0a0a0a", padding: isMobile ? "40px 20px" : "56px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
            <select value={queryTypeFilter} onChange={e => setQueryTypeFilter(e.target.value)} style={selectStyle}>
              {queryTypes.map(q => <option key={q} value={q}>{q === "All" ? "All Query Types" : q}</option>)}
            </select>
            <select value={industryFilter} onChange={e => setIndustryFilter(e.target.value)} style={selectStyle}>
              {industries.map(i => <option key={i} value={i}>{i === "All" ? "All Industries" : i}</option>)}
            </select>
            <select value={aiFilter} onChange={e => setAiFilter(e.target.value)} style={selectStyle}>
              <option value="All">AI Overview: All</option>
              <option value="Yes">AI Overview: Yes</option>
              <option value="No">AI Overview: No</option>
            </select>
            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "13px", fontFamily: ff, alignSelf: "center", marginLeft: "auto" }}>{filtered.length} keywords</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: ff }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                  {(["keyword", "queryType", "industry", "organicCTR", "kd"] as SortKey[]).map(k => (
                    <th key={k} style={thStyle} onClick={() => handleSort(k)}>
                      {k === "queryType" ? "Query Type" : k === "organicCTR" ? "Organic CTR" : k.charAt(0).toUpperCase() + k.slice(1)}
                      <SortArrow active={sortKey === k} dir={sortDir} />
                    </th>
                  ))}
                  <th style={thStyle}>AI Overview</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                    <td style={{ ...tdStyle, color: "white", fontSize: "14px", maxWidth: "240px" }}>{row.keyword}</td>
                    <td style={tdStyle}>
                      <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600, background: `${queryTypeColors[row.queryType]}20`, color: queryTypeColors[row.queryType] }}>{row.queryType}</span>
                    </td>
                    <td style={{ ...tdStyle, color: "rgba(255,255,255,0.55)", fontSize: "13px" }}>{row.industry}</td>
                    <td style={{ ...tdStyle, color: "rgba(255,255,255,0.8)", fontSize: "14px" }}>{row.organicCTR}%</td>
                    <td style={{ ...tdStyle, color: "rgba(255,255,255,0.7)", fontSize: "14px" }}>{row.kd}</td>
                    <td style={tdStyle}>
                      <span style={{ color: row.aiOverview ? "#ef4444" : "#22c55e", fontSize: "13px", fontWeight: 600 }}>{row.aiOverview ? "Yes" : "No"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Methodology */}
          <div style={{ marginTop: "48px", padding: "24px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px" }}>
            <h3 style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "10px" }}>Methodology</h3>
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", fontFamily: ff, lineHeight: 1.7, margin: 0 }}>
              80 B2B question-format keywords (20 per query type) were checked live in US Google Search, March 2026. Query types were assigned based on the keyword's leading modifier. Organic CTR figures are estimated from Ahrefs data adjusted for AI Overview presence. This dataset focuses on question-format keywords specifically — navigational and transactional queries are excluded. See our full{" "}
              <a href="/studies/ctr-study-2026" style={{ color: cyan, textDecoration: "none" }}>CTR study methodology</a>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "#0c0c0c", borderTop: "1px solid rgba(255,255,255,0.05)", padding: isMobile ? "60px 20px" : "80px 72px" }}>
        <div style={{ maxWidth: "640px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ color: "white", fontSize: isMobile ? "26px" : "36px", fontWeight: 700, fontFamily: ff, lineHeight: 1.15, marginBottom: "16px" }}>Which content formats should you stop writing?</h2>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "17px", fontFamily: ff, lineHeight: 1.6, marginBottom: "32px" }}>Not all B2B content is equally affected by AI Overviews. We help companies identify where to redirect content investment — away from query types being captured by Google, toward formats that still drive clicks.</p>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0" }}>
            <a href={STRATEGIST_URL} style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "16px 32px", background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)", borderRadius: "88px", border: "1px solid #57d7e2", boxShadow: "0px 4px 20px rgba(87,215,226,0.25)", textDecoration: "none", color: "#0a0c0c", fontSize: "15px", fontWeight: 700, fontFamily: ff, transition: "opacity 0.2s, transform 0.2s" }} onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-1px)"; }} onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}>
              Get my free video review →
            </a>
            <a
              href="/ai-seo"
              style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", fontFamily: ff, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "5px", marginTop: "16px" }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = "#57d7e2"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = "rgba(255,255,255,0.4)"}
            >
              Learn about our AI SEO service
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
